import React, { useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import * as echarts from 'echarts';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import Visualis from './components/Visualis';
import Tables from './components/Tables';
import { StoreRankDetailsCoreContext } from './context';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';

import './index.scss';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IEChartInstance, IEChartOption } from '@/pages/intelligence/types/echart';
import type {
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountriesRanks,
} from '@/pages/intelligence/types/storeRank';
import type { IVisualisOption } from './components/Visualis';
import type { ITablesOption } from './components/Tables';
import type { IStoreRankDetailsCoreInstance } from './types';

interface IStoreRankDetailsCoreProps extends IBaseProps {
  isLoading: boolean;
  gameId: string;
  title: string;
  countries: any[];
  visualisOption: IVisualisOption;
  tablesOption: ITablesOption;
  pageLogConfig?: any;
  downloadBtn?: React.ReactElement;
  selectedMode?: boolean | 'single' | 'multiple';
  fetchCountriesRankData?: (
    record: IStoreRankDetailsTablesSourceData,
  ) => Promise<IStoreRankDetailsCountriesRanks[]>;
  entityType?: string;
  isNewOpenTable?: boolean;
  gameName?: string;
  gameCover?: string;
  dateType?: string;
  startDate?: string;
  endDate?: string;
}

function StoreRankDetailsCore(
  props: IStoreRankDetailsCoreProps,
  instance: React.Ref<IStoreRankDetailsCoreInstance>,
) {
  const {
    isLoading,
    gameId,
    title,
    countries,
    visualisOption,
    tablesOption,
    pageLogConfig,
    downloadBtn,
    selectedMode = true,
    fetchCountriesRankData,
    entityType,
    currentFetchSource,
    isNewOpenTable = false, // 是否为新的下钻宽表
    gameName,
    gameCover,
    dateType,
    startDate,
    endDate,
  } = props;

  const root = useRef<HTMLDivElement>(null);

  const isNodata = !(
    visualisOption?.sourceDataDimensions?.length > 0 &&
    visualisOption?.sourceData?.length > 0 &&
    tablesOption?.sourceData?.length > 0
  );

  const getEChartInstance = (): IEChartInstance | null | undefined => {
    if (!root.current) return null;
    // REMARK: 优化echart实例获取的方式，目前封装的echart组件层级较深，增加ref引用比较麻烦，暂时先通过这种方式获取
    const chartNode = root.current?.querySelector('.chart-box div[_echarts_instance_]');
    if (!chartNode) return null;
    return echarts.getInstanceByDom(chartNode as HTMLElement);
  };

  const getEChartOption = (): IEChartOption | null => {
    const instance = getEChartInstance();
    if (!instance) return null;
    return instance.getOption();
  };

  useImperativeHandle(instance, () => ({
    getEChartInstance,
    getEChartOption,
  }));

  useEffect(() => {
    if (!pageLogConfig?.pageId) return;
    buriedPointGame(pageLogConfig, gameId, 'edition_id');
  }, []);

  return (
    <StoreRankDetailsCoreContext.Provider
      value={{
        showRankName: tablesOption?.showRankName,
        countries,
        fetchCountriesRankData,
        entityType,
        gameName,
        gameCover,
        dateType,
        startDate,
        endDate,
      }}
    >
      <div ref={root} className="dbi-markets-card dbi-store-rank-details-core">
        <Abnormal
          style={{ backgroundColor: '#fff', borderRadius: 8 }}
          isLoading={isLoading}
          isNoData={isNodata}
          component={
            <>
              <Visualis
                {...visualisOption}
                isLoading={isLoading}
                title={title}
                selectedMode={selectedMode}
                entityType={entityType}
                currentFetchSource={currentFetchSource}
              />
              <Tables
                {...tablesOption}
                isLoading={isLoading}
                pageLogConfig={pageLogConfig}
                downloadBtn={downloadBtn}
                entityType={entityType}
                isNewOpenTable={isNewOpenTable}
                gameName={gameName}
                gameCover={gameCover}
                dateType={dateType}
                startDate={startDate}
                endDate={endDate}
              />
            </>
          }
        />
      </div>
    </StoreRankDetailsCoreContext.Provider>
  );
}

export default forwardRef(StoreRankDetailsCore);
